import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useClientUser } from '@/hooks';
import { carrierDisplayName } from '@/utilities/carrierAlias';
import { useMutation, useQuery } from '@apollo/client';
import { QUERY_ORDERS, UPSERT_CLAIM } from './graphql';
import { UserContext } from '@/components/App';
import {
    ModalContent,
    ModalTitle,
    PrimaryButton,
    ResponsiveSidebarDialog,
    SecondaryButton,
    StickyModalActions,
} from '@/styles/blocks';
import { css } from '@emotion/react';
import { captureException } from '@sentry/react';
import { Grid, MenuItem, TextField, Typography } from '@material-ui/core';
import { AsyncOrderAutocomplete } from './blocks';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import { format } from 'date-fns';
import { asBrowserDate } from '@/utilities/convertToISO';
import { CLAIM_TYPES, ITEM_TYPE_REASONS } from '../../constants';
import ClaimItem from './ClaimItem';
import { MARKETPLACE_TIERS } from '@onward-delivery/core';

const REQUIRED_CLAIM_FIELDS = ['order_id', 'claim_type', 'requested_by', 'freight_amount', 'incident_date'];
const REQUIRED_CLAIM_ITEM_BASE_FIELDS = ['reason', 'amount', 'description'];
const REQUIRED_CLAIM_ITEM_FIELDS = {
    FREIGHT: [...REQUIRED_CLAIM_ITEM_BASE_FIELDS, 'documents'],
    PROPERTY_DAMAGE: [...REQUIRED_CLAIM_ITEM_BASE_FIELDS, 'documents'],
    SERVICE_FAILURE: [...REQUIRED_CLAIM_ITEM_BASE_FIELDS],
};

const CLAIM_UPDATED_HASH_ATTRS = [
    'claim_type',
    'requested_by',
    'freight_amount',
    'incident_date',
    'order_id',
    'shipper_id',
    'carrier_id',
    'middle_mile_carrier_id',
];
const CLAIM_ITEMS_UPDATED_HASH_ATTRS = ['item_id', 'reason', 'amount', 'description', 'images', 'documents'];

const Claims = ({ claim, open, onClose, onSave, callbacks }) => {
    const [updates, setUpdates] = useState({});
    const { user_id, isOnwardAdmin, partnerCarriersById } = useClientUser();
    const { admin_clients } = useContext(UserContext);
    const [ordersSearch, setOrdersSearch] = useState(null);

    useEffect(() => {
        setUpdates(claim || {});
    }, [claim]);

    const updated = useMemo(() => {
        return {
            ...(claim || {}),
            ...updates,
        };
    }, [updates, claim]);

    const hideTier3 = useMemo(() => {
        const isTier3 =
            !updated.order?.oms &&
            updated.order?.shipper_rate_override?.marketplace_tier === MARKETPLACE_TIERS.T3_FULLY_MANAGED;

        return isTier3 && !isOnwardAdmin;
    }, [updated]);

    const isValid = useMemo(() => {
        return (
            REQUIRED_CLAIM_FIELDS.every((attr) => !!updated[attr] || updated[attr] === 0) &&
            !!updated.items?.length &&
            (updated.items || []).every((item) => {
                let requiredFields = REQUIRED_CLAIM_ITEM_FIELDS[updated.claim_type] || REQUIRED_CLAIM_ITEM_BASE_FIELDS;
                if (item.reason?.includes('damage')) {
                    requiredFields.push('images');
                }
                return requiredFields.every((attr) => !!item[attr] || item[attr] === 0);
            })
        );
    }, [updated]);

    const canAddMoreItems = useMemo(() => {
        switch (updated.claim_type) {
            case 'FREIGHT':
                return updated.items?.length < updated.order?.itemsByOrderId?.length;
            case 'PROPERTY_DAMAGE':
                return true;
            case 'SERVICE_FAILURE':
                return false;
        }
    }, [updated]);

    const client_ids = user_id ? [user_id] : (admin_clients || []).map((client) => client.client_id);
    const { data: ordersData, loading: ordersLoading } = useQuery(QUERY_ORDERS, {
        skip: !ordersSearch,
        variables: {
            where: {
                _and: [
                    ...(isOnwardAdmin ? [{ oms: { _eq: false } }] : [{ shipper_id: { _in: client_ids } }]),
                    {
                        _or: [
                            { order_number: { _ilike: `%${ordersSearch?.split(' / ')?.[0]}%` } },
                            { po_number: { _ilike: `%${ordersSearch?.split(' / ')?.[0]}%` } },
                        ],
                    },
                    { order_status: { _nin: ['incomplete', 'deleted'] } },
                ],
            },
        },
        onError: (error) => {
            console.error(error);
            captureException(error);
            callbacks.setNotification({
                severity: 'error',
                message: `Failed to search orders. ${error.message}`,
            });
        },
    });

    const ordersByKey = useMemo(() => {
        return Object.fromEntries(
            (ordersData?.orders || (updated?.order ? [updated.order] : []))
                .filter((order) => !!order)
                .map((order) => [order.order_id, order])
        );
    }, [ordersData, updated]);

    const [upsertClaim, { loading: saveInFlight }] = useMutation(UPSERT_CLAIM, {
        onError: (error) => {
            console.error(error);
            captureException(error);
            callbacks.setNotification({
                severity: 'error',
                message: `Failed to save claim. ${error.message}`,
            });
        },
    });

    const saveClaim = () => {
        const new_claim_items = updated?.items?.map((item) => item.claim_item_id);
        const claim_item_removals = claim?.items
            ?.map((item) => item.claim_item_id)
            .filter((id) => !!id && !new_claim_items.includes(id));

        const { __typename, order, shipper, carrier, middle_mile_carrier, items, adjustments, notes, ...toSave } =
            updated;
        const claimItems = items.map((claim_item) => {
            const { __typename, item, ...rest } = claim_item;
            return rest;
        });
        let claim_items = [];

        if (!!toSave.claim_id) {
            claim_items = claimItems.map((item) => ({ ...item, claim_id: toSave.claim_id }));
        } else {
            toSave.items = { data: claimItems };
        }

        const hash = [
            ...CLAIM_UPDATED_HASH_ATTRS.map((attr) => (toSave[attr] || '').toString()),
            ...(claimItems || []).reduce((acc, item) => {
                return [...acc, ...CLAIM_ITEMS_UPDATED_HASH_ATTRS.map((attr) => (item[attr] || '').toString())];
            }, []),
        ].join(':');

        upsertClaim({
            variables: {
                claim: {
                    ...toSave,
                    details_hash: btoa(encodeURIComponent(hash)),
                },
                claim_items,
                claim_item_removals,
            },
        }).then(({ data }) => {
            onSave(data?.insert_claims_one);
        });
    };

    return (
        <ResponsiveSidebarDialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            css={css`
                .MuiDialog-container {
                    align-items: normal;
                }
            `}
        >
            <ModalTitle title={claim?.claim_number ? `Claim ${claim.claim_number}` : 'New Claim'} onClose={onClose} />
            <ModalContent
                css={css`
                    width: 100%;
                `}
            >
                <Grid container direction="column" css={css``}>
                    <Grid
                        item
                        css={css`
                            margin-bottom: 1rem;
                        `}
                    >
                        <AsyncOrderAutocomplete
                            label="Order"
                            placeholder="Search Orders by OD# or PO#"
                            options={ordersByKey}
                            loading={ordersLoading}
                            onChange={(order) => {
                                if (!!order && order?.order_id !== updated.order_id) {
                                    setUpdates((prev) => ({
                                        ...prev,
                                        order_id: order.order_id,
                                        order,
                                        shipper_id: order.shipper_id,
                                        carrier_id: order.middle_mile_carrier_id ? null : order.carrier_id,
                                        middle_mile_carrier_id: null,
                                        items: [{}],
                                    }));
                                }
                            }}
                            value={updated.order_id}
                            onSearch={(search) => setOrdersSearch(search)}
                        />
                    </Grid>
                    {updated?.order?.claims && updated?.order?.claims?.length > 0 && (
                        <>
                            <Grid item>
                                <Typography
                                    css={css`
                                        font-weight: 500;
                                    `}
                                >
                                    Warning: This order already has the following claims associated with it:
                                </Typography>
                            </Grid>
                            {updated.order.claims.map((existingClaim, index) => (
                                <Grid
                                    item
                                    key={existingClaim.claim_id}
                                    css={css`
                                        ${index === updated.order.claims.length - 1 && `margin-bottom: 1rem;`}
                                    `}
                                >
                                    <Typography
                                        css={css`
                                            font-weight: 500;
                                        `}
                                    >
                                        Claim {existingClaim.claim_number} - Status: {existingClaim.claim_status}
                                    </Typography>
                                </Grid>
                            ))}
                        </>
                    )}
                    {!!updated.order?.middle_mile_carrier_id ? (
                        <Grid
                            container
                            css={css`
                                margin-bottom: 1rem;
                            `}
                        >
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    variant="outlined"
                                    label="Carrier"
                                    value={updated.middle_mile_carrier_id || updated.carrier_id || ''}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        if (!id) {
                                            setUpdates((prev) => ({
                                                ...prev,
                                                carrier_id: null,
                                                middle_mile_carrier_id: null,
                                            }));
                                        } else if (id === updated.order.carrier_id) {
                                            setUpdates((prev) => ({
                                                ...prev,
                                                carrier_id: id,
                                                middle_mile_carrier_id: null,
                                            }));
                                        } else {
                                            setUpdates((prev) => ({
                                                ...prev,
                                                carrier_id: null,
                                                middle_mile_carrier_id: id,
                                            }));
                                        }
                                    }}
                                    error={!updated.carrier_id && !updated.middle_mile_carrier_id}
                                >
                                    <MenuItem value={updated.order.carrier_id}>
                                        {hideTier3
                                            ? 'Final Mile Carrier'
                                            : `Final Mile Agent (${carrierDisplayName(
                                                  updated.order.order_carrier,
                                                  partnerCarriersById
                                              )})`}
                                    </MenuItem>
                                    <MenuItem value={updated.order.middle_mile_carrier_id}>
                                        {hideTier3
                                            ? 'Middle Mile Carrier'
                                            : `Middle Mile Agent (${carrierDisplayName(
                                                  updated.order.order_middle_mile_carrier,
                                                  partnerCarriersById
                                              )})`}
                                    </MenuItem>
                                    <MenuItem value={null}>Unknown</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    ) : null}
                    <Grid
                        container
                        css={css`
                            margin-bottom: 1rem;
                        `}
                    >
                        <Grid
                            item
                            xs={6}
                            css={css`
                                padding-right: 0.5rem;
                            `}
                        >
                            <TextField
                                fullWidth
                                variant="outlined"
                                label="Requested By"
                                value={updated.requested_by}
                                onChange={(e) => setUpdates((prev) => ({ ...prev, requested_by: e.target.value }))}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={6}
                            css={css`
                                padding-left: 0.5rem;
                            `}
                        >
                            <CurrencyTextField
                                fullWidth
                                variant="outlined"
                                label="Requested Freight Amount"
                                value={updated.freight_amount || ''}
                                currencySymbole="$"
                                outputFormat="number"
                                error={!(updated.freight_amount >= 0)}
                                onChange={(_, val) => setUpdates((prev) => ({ ...prev, freight_amount: val }))}
                            />
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        css={css`
                            margin-bottom: 1rem;
                        `}
                    >
                        <Grid
                            item
                            xs={6}
                            css={css`
                                padding-right: 0.5rem;
                            `}
                        >
                            <TextField
                                fullWidth
                                type="date"
                                variant="outlined"
                                label="Incident Date"
                                value={
                                    updated.incident_date
                                        ? format(asBrowserDate(updated.incident_date), 'yyyy-MM-dd')
                                        : ''
                                }
                                onChange={(e) => {
                                    let dateStr = null;
                                    if (e.target.value) {
                                        dateStr = new Date(
                                            new Date(e.target.value).setUTCHours(0, 0, 0, 0)
                                        ).toISOString();
                                    }
                                    setUpdates((prev) => ({ ...prev, incident_date: dateStr }));
                                }}
                                InputLabelProps={{ shrink: true }}
                                error={!updated.incident_date}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={6}
                            css={css`
                                padding-left: 0.5rem;
                            `}
                        >
                            <TextField
                                select
                                fullWidth
                                variant="outlined"
                                label="Claim Type"
                                value={updated.claim_type || ''}
                                onChange={(e) =>
                                    setUpdates((prev) => ({ ...prev, claim_type: e.target.value, items: [{}] }))
                                }
                                error={!updated.claim_type}
                            >
                                {Object.entries(CLAIM_TYPES).map(([key, value]) => (
                                    <MenuItem key={key} value={key}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                    {updated.items?.map((item, index) => (
                        <ClaimItem
                            key={index}
                            index={index}
                            order={updated.order}
                            claim={updated}
                            claim_item={item}
                            callbacks={{
                                updateItem: (newItem) => {
                                    setUpdates((prev) => ({
                                        ...prev,
                                        items: prev.items.map((_, i) => (i === index ? newItem : _)),
                                    }));
                                },
                                removeItem: () => {
                                    setUpdates((prev) => ({
                                        ...prev,
                                        items: prev.items.filter((_, i) => i !== index),
                                    }));
                                },
                            }}
                        />
                    ))}
                    {canAddMoreItems ? (
                        <Grid container>
                            <Grid item>
                                <PrimaryButton
                                    disabled={saveInFlight}
                                    onClick={() => setUpdates((prev) => ({ ...prev, items: [...prev.items, {}] }))}
                                >
                                    Add Item
                                </PrimaryButton>
                            </Grid>
                        </Grid>
                    ) : null}
                </Grid>
            </ModalContent>
            <StickyModalActions>
                <Grid item>
                    <SecondaryButton disabled={saveInFlight} onClick={onClose}>
                        Cancel
                    </SecondaryButton>
                </Grid>
                <Grid item>
                    <PrimaryButton disabled={saveInFlight || !isValid} onClick={saveClaim}>
                        Save
                    </PrimaryButton>
                </Grid>
            </StickyModalActions>
        </ResponsiveSidebarDialog>
    );
};

export default Claims;
